/**
 * Utility functions for Supplier Performance module
 */

import { SupplierProfile, SupplierRatingThreshold, SupplierCustomerSite } from '../../lib/api';
import { GradeInfo } from './types';

// CR-7079: the Rating column now shows the tenant's configured grade — its
// label and its colour — rather than a fixed map.
//
// CR-5986 r5 previously pinned this column to {Gold, Silver, Bronze, Poor}
// with hardcoded hexes, keyed off the grade letter, to stop tenant labels like
// 'At Risk' bleeding into the table. That map also took precedence over the
// configured colour, so `threshold.color` was unreachable here: a grade
// configured as metallic Gold still rendered green, which is the bug this CR
// was raised for. Sarah confirmed on 9 Sep that the configured scheme is what
// both scorecard customers want to see, so the lock is lifted for label and
// colour together — showing a configured 'Platinum' colour under a hardcoded
// 'Gold' label would be worse than either.
//
// Colour comes from `effective_color`, which the API resolves from the
// company's shared grade palette (falling back to the band's own `color` for a
// custom grade). That is what makes one palette change cascade to the Rating
// column, the dashboard gauges and the PPM bands at once.

// Used only when a company has no configured bands at all, so there is nothing
// to read a label or colour from.
const UNCONFIGURED_FALLBACK: Record<string, { label: string; color: string }> = {
  A: { label: 'Platinum', color: '#E5E4E2' },
  B: { label: 'Gold', color: '#D4AF37' },
  C: { label: 'Silver', color: '#C0C0C0' },
  D: { label: 'Bronze', color: '#CD7F32' },
  F: { label: 'Not Rated', color: '#000000' },
};

function fallbackFromGrade(grade: string): GradeInfo {
  const entry = UNCONFIGURED_FALLBACK[grade.toUpperCase()];
  return entry
    ? { grade, label: entry.label, color: entry.color }
    : { grade, label: grade, color: '#000000' };
}

function fromThreshold(threshold: SupplierRatingThreshold): GradeInfo {
  return {
    grade: threshold.grade,
    label: threshold.display_label || threshold.label || threshold.grade,
    color: threshold.effective_color || threshold.color,
  };
}

/**
 * Get grade info based on combined score and configured thresholds.
 *
 * Customer review 11 Sep (item 2): `ratingThresholds` must be the viewer's
 * configured Grade Thresholds bands from `useRatingBands()` — the ONE source
 * every rating badge reads (see hooks/useRatingBands.ts for why those bands,
 * and not the OTIF grade scale). The empty-list fallback below exists only for
 * a company with no bands configured at all.
 */
export function getGradeInfo(
  combinedScore: number | undefined | null,
  ratingThresholds: SupplierRatingThreshold[]
): GradeInfo {
  const score = combinedScore ?? 0;

  // Find matching threshold
  for (const threshold of ratingThresholds) {
    const minScore = parseFloat(threshold.min_score);
    const maxScore = parseFloat(threshold.max_score);
    if (score >= minScore && score < maxScore) {
      return fromThreshold(threshold);
    }
  }

  // Fallback: check if score equals max of any threshold (for 100%)
  for (const threshold of ratingThresholds) {
    const maxScore = parseFloat(threshold.max_score);
    if (score === maxScore) {
      return fromThreshold(threshold);
    }
  }

  // Default fallback if no thresholds configured
  if (ratingThresholds.length === 0) {
    if (score >= 90) return fallbackFromGrade('A');
    if (score >= 75) return fallbackFromGrade('B');
    if (score >= 60) return fallbackFromGrade('C');
    if (score >= 40) return fallbackFromGrade('D');
    return fallbackFromGrade('F');
  }

  // Return lowest grade if nothing matches
  const lowest = ratingThresholds[ratingThresholds.length - 1];
  return lowest ? fromThreshold(lowest) : fallbackFromGrade('F');
}

/**
 * Get letter grade from OTIF score
 */
export function getLetterGrade(
  otifScore: number | undefined,
  ratingThresholds: SupplierRatingThreshold[]
): string {
  return getGradeInfo(otifScore, ratingThresholds).label;
}

const DARK_TEXT = '#1F2937';
const LIGHT_TEXT = '#FFFFFF';
const LIGHT_BORDER = '1px solid rgba(0, 0, 0, 0.18)';

/**
 * Relative luminance of a #RRGGBB colour, per WCAG 2.1.
 * Returns 0 (black) to 1 (white), or null if the value isn't a hex colour.
 */
function luminance(hex: string): number | null {
  const match = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!match) return null;
  const int = parseInt(match[1], 16);
  const channels = [(int >> 16) & 255, (int >> 8) & 255, int & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

/** WCAG contrast ratio between two relative luminances. */
function contrast(a: number, b: number): number {
  const hi = Math.max(a, b);
  const lo = Math.min(a, b);
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Get rating badge style.
 *
 * CR-7079: the grade colour is now the badge's *fill*, with the text set to
 * black or white by luminance.
 *
 * It used to be the text colour on a 12%-alpha wash of itself. That reads
 * fine for saturated traffic-light colours, but the confirmed scorecard
 * palette is metallic — Platinum #E5E4E2 as text on #E5E4E220 is about 1.1:1
 * contrast, i.e. an invisible badge on a white row. Filling the badge instead
 * keeps every configured colour legible, including a customer's own, and
 * matches how the Grade Thresholds settings page already draws its swatches.
 *
 * A light fill gets a slightly darkened border so the badge still has an edge
 * against the white table row.
 */
export function getRatingStyle(
  otifScore: number | undefined,
  ratingThresholds: SupplierRatingThreshold[]
): RatingBadgeStyle {
  return ratingBadgeStyle(getGradeInfo(otifScore, ratingThresholds).color);
}

export interface RatingBadgeStyle {
  backgroundColor: string;
  color: string;
  border: string;
}

/**
 * The badge style for one grade colour.
 *
 * Exported so every scorecard that shows a rating badge — Supplier
 * Performance, the supplier list card, the supplier detail header and the
 * rating breakdown modal — draws it identically. Each of those used to inline
 * its own `${color}20` tint, which is how they drifted apart.
 */
export function ratingBadgeStyle(gradeColour: string): RatingBadgeStyle {
  const info = { color: gradeColour };
  const lum = luminance(info.color);
  // Unparseable colour: dark text, so a bad config shows up rather than
  // silently rendering white on white.
  if (lum === null) {
    return {
      backgroundColor: info.color,
      color: DARK_TEXT,
      border: LIGHT_BORDER,
    };
  }
  // Pick whichever foreground actually contrasts better against this fill
  // rather than splitting on a fixed luminance. A mid-tone like Bronze
  // #CD7F32 sits close to the middle, and choosing by measured ratio takes it
  // from 3.1:1 to 6.7:1 — the difference between failing and passing WCAG AA.
  const onDark = contrast(lum, luminance(DARK_TEXT) as number);
  const onLight = contrast(lum, luminance(LIGHT_TEXT) as number);
  const useDark = onDark >= onLight;
  return {
    backgroundColor: info.color,
    color: useDark ? DARK_TEXT : LIGHT_TEXT,
    // A pale fill needs an edge against the white table row; a dark one doesn't.
    border: lum > 0.7 ? LIGHT_BORDER : '1px solid transparent',
  };
}

/**
 * Get OTD color class based on rate
 */
export function getOTDColor(rate: number): string {
  if (rate >= 90) return 'text-green-600 dark:text-green-400';
  if (rate >= 80) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}

/**
 * Format currency value from backend.
 * Backend returns values already converted to user's preferred currency.
 */
export function formatCurrency(value: string | null, currency: string): string {
  if (!value) return '0';

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return '0';

  // Known ISO symbols; anything else falls back to a sanitised 3-letter
  // code so a stray/garbled value (e.g. a supplier's free-text currency
  // field) can't render junk like "SLO" in front of the number.
  const SYMBOLS: Record<string, string> = {
    GBP: '£',
    EUR: '€',
    USD: '$',
    JPY: '¥',
    CNY: '¥',
    AUD: 'A$',
    CAD: 'C$',
    INR: '₹',
  };
  const code = (currency || '').trim().toUpperCase();
  const isIsoCode = /^[A-Z]{3}$/.test(code);
  const currencySymbol = SYMBOLS[code] ?? (isIsoCode ? `${code} ` : '');

  // INR is read in lakhs/crores, not millions.
  if (code === 'INR') {
    if (numValue >= 10000000) return `${currencySymbol}${(numValue / 10000000).toFixed(2)}Cr`;
    if (numValue >= 100000) return `${currencySymbol}${(numValue / 100000).toFixed(2)}L`;
    return `${currencySymbol}${numValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }
  if (numValue >= 1000000) {
    return `${currencySymbol}${(numValue / 1000000).toFixed(2)}M`;
  } else if (numValue >= 1000) {
    return `${currencySymbol}${(numValue / 1000).toFixed(2)}K`;
  }

  return `${currencySymbol}${numValue.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
}

/**
 * The Customer column's text for a row — Customer review 11 Sep (item 3).
 *
 * The row is the supplier as a whole over every site in `sites`, so one site is
 * named and several are summarised; the full list goes in the tooltip.
 *
 * @param sites - the customer sites the row's figures cover
 * @returns `{ label, title }` — `title` lists every site with its supplier code
 */
export function customerScopeLabel(sites: SupplierCustomerSite[] | undefined): { label: string; title: string } {
  const list = sites ?? [];
  if (list.length === 0) return { label: '-', title: 'No active customer site in this view' };
  const title = list
    .map((s) => (s.supplier_code ? `${s.name} (code ${s.supplier_code})` : s.name))
    .join('\n');
  if (list.length === 1) return { label: list[0].name, title };
  return { label: `All sites (${list.length})`, title };
}

/**
 * A band's range as the Grade Thresholds tab shows it, e.g. "75–90".
 *
 * @param threshold - one configured rating band
 */
export function bandRange(threshold: SupplierRatingThreshold): string {
  const fmt = (v: string) => String(parseFloat(v));
  return `${fmt(threshold.min_score)}–${fmt(threshold.max_score)}`;
}

/**
 * Check if supplier has N/A rating (no combined score data)
 */
export function isNARating(supplier: SupplierProfile): boolean {
  return supplier.combined_score === null || supplier.combined_score === undefined;
}
